// Dashboard profesor: saludo + grid de cards de próximas clases asignadas.
// Reusa clases CSS del admin (.admin-page, .admin-section, .admin-table).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, Loader2, Calendar, Users, ChevronRight, MapPin, CalendarPlus,
} from 'lucide-react';

import { getProfesorMe, getProfesorClasesAsignadas } from '../../lib/profesorApi';

function formatFechaLarga(iso, lang) {
  // iso es "YYYY-MM-DD"; construyo Date local para que weekday sea correcto.
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const locales = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  const f = new Intl.DateTimeFormat(locales[lang] || 'es-ES', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  const s = f.format(date);
  return s.charAt(0).toLocaleUpperCase(locales[lang]) + s.slice(1);
}

export default function ProfesorDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.resolvedLanguage || 'es';
  const [me, setMe] = useState(null);
  const [clases, setClases] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getProfesorMe(lang), getProfesorClasesAsignadas({ idioma: lang })])
      .then(([m, c]) => { if (!cancelled) { setMe(m); setClases(c); } })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang]);

  if (loading) {
    return <div className="admin-loading"><Loader2 size={28} className="spin" /> <span>{t('admin.cargando')}</span></div>;
  }
  if (error) {
    return <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>;
  }
  if (!me || !clases) return null;

  const data = clases.data || [];
  const total = clases.total || 0;
  const nombre = me.nombre_publico || (me.usuario?.nombre || '');

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1>{t('profesor.dashboard.saludo', { nombre })}</h1>
        <p className="admin-page-sub">{t('profesor.dashboard.sub', { count: total, days: 30 })}</p>
      </header>

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>{t('profesor.dashboard.proximas_titulo')}</h2>
        </div>
        {data.length === 0 ? (
          <p className="admin-empty">{t('profesor.dashboard.sin_clases')}</p>
        ) : (
          <div className="profesor-clases-grid">
            {data.map((c) => (
              <button
                key={c.sesion_id}
                type="button"
                className="profesor-clase-card"
                onClick={() => navigate(`/profesor/clase/${c.sesion_id}`)}
              >
                <div className="profesor-clase-fecha">
                  <Calendar size={14} strokeWidth={1.5} />
                  <span>{formatFechaLarga(c.fecha_clase, lang)}</span>
                </div>
                <div className="profesor-clase-hora">
                  {c.hora_inicio} – {c.hora_fin}
                </div>
                <div className="profesor-clase-tipo">
                  {c.clase_tipo?.nombre || '—'}
                  {c.rol_en_sesion === 'asignado' && (
                    <span className="profesor-pill profesor-pill-asignado">{t('profesor.dashboard.pill_asignado')}</span>
                  )}
                </div>
                <div className="profesor-clase-meta">
                  {c.sala && (
                    <span className="profesor-clase-meta-item">
                      <MapPin size={12} strokeWidth={1.5} /> {c.sala}
                    </span>
                  )}
                  <span className="profesor-clase-meta-item">
                    {t('profesor.formato.' + (c.formato || 'presencial'))}
                  </span>
                </div>
                <div className="profesor-clase-foot">
                  <span className="profesor-clase-alumnos">
                    <Users size={14} strokeWidth={1.5} />
                    {c.num_alumnos_inscritos} / {c.aforo_maximo}
                    <span className="profesor-clase-alumnos-label">{t('profesor.dashboard.alumnos')}</span>
                  </span>
                  <ChevronRight size={16} strokeWidth={1.5} />
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="profesor-cta-reservar">
        <div className="profesor-cta-reservar-info">
          <CalendarPlus size={20} strokeWidth={1.5} />
          <div>
            <strong>{t('profesor.dashboard.cta_reservar_titulo')}</strong>
            <span>{t('profesor.dashboard.cta_reservar_sub')}</span>
          </div>
        </div>
        <button
          type="button"
          className="btn-mini btn-primary"
          onClick={() => navigate('/profesor/reservar-como-alumno')}
        >
          {t('profesor.dashboard.cta_reservar_btn')}
        </button>
      </section>
    </div>
  );
}
